import React, {useCallback, useEffect, useState} from 'react'
import View, {FormProps} from './view'
import {Course, selectOrganization} from '../../redux/courses'
import {useAppDispatch} from '../../redux/configureStore'
import {useAuth} from '../AuthProvider'
import {toggleErrorDialog} from '../../redux/config'
import {
  confirmOnetimeProduct,
  getOnetimeProduct,
  selectProduct,
} from '../../redux/checkout'
import {useSelector} from 'react-redux'
import {selectMember} from '../../redux/members'
import {getCardDetails, selectCardDetails} from '../../redux/organizations'
import {Challenge, getActiveChallenge} from '../../redux/challenges'

type Props = {
  open: boolean
  setOpen: (val: boolean) => void
  courseId?: string
  challenge: Challenge
}

const Challenge30DaysDialog = ({open, setOpen, courseId, challenge}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isExamScheduled, setIsExamScheduled] = useState('')
  const [currentStep, setCurrentStep] = useState<
    'form' | 'payment' | 'success'
  >('form')

  const auth = useAuth()
  const dispatch = useAppDispatch()

  const product = useSelector(selectProduct)
  const member = useSelector(selectMember)
  const cardDetails = useSelector(selectCardDetails)
  const organization = useSelector(selectOrganization)

  const isGroupAccount =
    (member?.groupAccounts && member.groupAccounts.length > 0) ||
    member?.groupId === 1

  const courses = organization?.courses.filter(course => {
    return ['PcM', 'PjM', 'PA', 'PPD', 'PDD', 'CE'].includes(
      course.abbreviation,
    )
  })

  const loadProduct = useCallback(async () => {
    if (challenge.stripePriceId) {
      await dispatch(
        getOnetimeProduct({
          auth,
          priceId: challenge.stripePriceId,
        }),
      )
    }
  }, [dispatch])

  const handleSubmit = async (values: FormProps) => {
    try {
      if (values.division) {
        const selectedCourse = courses?.find(c => c.id === values.division)

        setIsSubmitting(true)
        const {type} = await dispatch(
          confirmOnetimeProduct({
            auth,
            product,
            courseId: values.division,
            isExamScheduled,
            courseName: selectedCourse?.title ?? '',
            challengeId: challenge.challengeId,
            processPayment: !isGroupAccount,
            paymentMethodId: cardDetails?.pmId ?? '',
          }),
        )

        if (type === confirmOnetimeProduct.rejected.type) {
          setIsSubmitting(false)
          throw new Error('Payment failure')
        } else {
          setIsSubmitting(false)
          setCurrentStep('success')
          await dispatch(getActiveChallenge({auth}))
        }
      }
    } catch (error) {
      await dispatch(
        toggleErrorDialog({
          opened: true,
          title: `Payment Issue`,
          error: `We couldn't process your payment. Please check your credit card details and try again. Let us know if you need any help!`,
        }),
      )
    }
  }

  const loadCardDetails = useCallback(async () => {
    await dispatch(getCardDetails({auth}))
  }, [dispatch])

  useEffect(() => {
    if (open) {
      loadProduct()
      loadCardDetails()
    }
  }, [loadProduct, loadCardDetails, open])

  return (
    <View
      open={open}
      setOpen={setOpen}
      onSubmit={handleSubmit}
      product={product}
      courses={courses}
      courseId={courseId}
      isSubmitting={isSubmitting}
      onChangeExamScheduled={(value: string) => setIsExamScheduled(value)}
      onChangeCurrentStep={(value: 'form' | 'payment' | 'success') =>
        setCurrentStep(value)
      }
      currentStep={currentStep}
      cardDetails={cardDetails}
      isGroupAccount={isGroupAccount}
    />
  )
}

export default Challenge30DaysDialog
